import {Component, ElementRef, OnInit, QueryList, ViewChildren} from '@angular/core';
import {ProductService} from '@services/product.service';
import {Product} from '@services/model/product';
import {CycleService} from '@services/cycle.service';
import {Cycle} from '@services/model/cycle';
import {MatDialog} from '@angular/material/dialog';
import {ProductPublishComponent, ProductPublishData} from '@dashboard/product-publish/product-publish.component';
import {filter, map, share, shareReplay, switchMap, tap} from 'rxjs/operators';
import {VoilaUserService} from '@services/voila-user.service';
import {User} from '@services/model/user';
import {ApplicationService} from '@services/application.service';
import {BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {VoteService} from '@services/vote.service';
import {UUID} from '@util/types';
import {Application} from '@services/model/application';
import {isSome, Option} from 'fp-ts/lib/Option';
import {repeatIf} from '@util/rxjs';
import {MyVotesResource} from '@services/resource/my-votes-resource';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {CommonModule} from '@angular/common';
import {StatusPanelComponent} from '@dashboard/welcome/status-panel/status-panel.component';
import {StepsPanelComponent} from '@dashboard/welcome/steps-panel/steps-panel.component';
import {VotesPanelComponent} from '@dashboard/welcome/votes-panel/votes-panel.component';
import {JurySectionComponent} from '@dashboard/welcome/jury-section/jury-section.component';
import {MatAccordion, MatExpansionPanel, MatExpansionPanelHeader} from '@angular/material/expansion';

export const dasTeamText =
  'Der Endverbraucher ist ständig auf der Suche nach neuen, innovativen\n' +
  '        Produkten und Ernährungstrends. Dies hat unsere Händler und\n' +
  '        die Markant dazu veranlasst, Ideen zu finden, um Händler und\n' +
  '        Start-ups schnell zusammen zu bringen. Daraus ist voilà! - Die\n' +
  '        Start-up Arena entstanden. Wir sind ein kleines, dynamisches Team aus\n' +
  '        dem Vermittlungsgeschäft der Markant Deutschland GmbH, welches dir\n' +
  '        für Fragen gerne zur Seite steht.\n' +
  '        Sowohl bei technischen Anliegen als auch bei allen anderen Fragen erreichst Du uns per E-Mail unter:';

@Component({
  selector: 'app-welcome',
  templateUrl: './welcome.component.html',
  styleUrls: ['./welcome.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTabGroup,
    MatTab,
    StatusPanelComponent,
    StepsPanelComponent,
    VotesPanelComponent,
    JurySectionComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
  ],
})
export class WelcomeComponent implements OnInit {
  private readonly reloadApplications$: Subject<void> = new BehaviorSubject<void>(undefined);

  public product$: Observable<Product>;
  public cycles$: Observable<Cycle[]>;
  public selectedIndex$: ReplaySubject<number> = new ReplaySubject<number>(1);
  public selectedCycle$: Observable<Cycle>;
  public selectedApplication$: Observable<Option<Application>>;
  @ViewChildren('mat-tab-label') tabLabel: QueryList<ElementRef>;
  public selectedVotes$: Observable<MyVotesResource | 'noApplication'>;
  public dasTeamText = dasTeamText;

  constructor(
    private readonly userService: VoilaUserService,
    private readonly productService: ProductService,
    private readonly voteService: VoteService,
    private readonly cycleService: CycleService,
    private readonly applicationService: ApplicationService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.product$ = this.productService.productForUser(this.user.id).pipe(share());

    this.cycles$ = this.cycleService.getAll(false).pipe(
      tap((x) => this.selectedIndex$.next(x.length - 1)), // open the latest tab
      share(),
    );

    this.selectedCycle$ = combineLatest([this.cycles$, this.selectedIndex$]).pipe(
      map((x) => x[0][x[1]]),
      shareReplay(1),
    );

    this.selectedApplication$ = combineLatest([this.product$, this.selectedCycle$]).pipe(
      repeatIf(this.reloadApplications$),
      switchMap((x) => {
        const product = x[0];
        const cycle = x[1];
        return this.applicationService.readApplication(product.id as string, cycle.uuid as string);
      }),
      share(),
    );

    this.selectedVotes$ = combineLatest([this.product$, this.selectedApplication$]).pipe(
      switchMap((x) => {
        const product = x[0];
        const application = x[1];
        if (isSome(application)) {
          return this.voteService.getVotesForOwnProduct(application.value.cycle.uuid as string, product.id as string);
        } else {
          return of('noApplication' as const);
        }
      }),
      share(),
    );
  }

  openPublish(productId: UUID): void {
    const data: ProductPublishData = {productId};
    const d = this.dialog.open(ProductPublishComponent, {width: '400px', data});
    d.afterClosed()
      .pipe(
        filter((r) => !!r),
        switchMap((cycleId) => this.applicationService.requestApproval(productId, cycleId)),
      )
      .subscribe(() => {
        this.reloadApplications$.next();
      });
  }

  selectCycleByIndex(e: any): void {
    this.selectedIndex$.next(e.index);
  }

  get user(): User {
    return this.userService.currentUser();
  }
}
